import { useSyncExternalStore } from "react";
import { getAccess } from "@/lib/api";
import { isOffline, listOffline, removeTrack, saveTrack } from "@/lib/offline";
import type { Track } from "@/types/types";

/** Whole-playlist downloads for offline listening, on top of lib/offline.ts
 *  (which stores the audio). Runs in the background across page changes,
 *  two tracks at a time, and remembers each saved track's metadata so the
 *  "Téléchargés" page can list and play them with no network at all. */

const META_KEY = "sl.offline.meta";
const CONCURRENCY = 2;

type Job = { key: string; label: string; ids: string[]; done: number; failed: number };
type Snapshot = { jobs: Job[]; version: number };

let snap: Snapshot = { jobs: [], version: 0 };
const listeners = new Set<() => void>();
const queue: { job: Job; track: Track }[] = [];
const cancelled = new Set<string>();
let running = 0;

function emit() {
  snap = { jobs: snap.jobs.map((j) => ({ ...j })), version: snap.version + 1 };
  listeners.forEach((l) => l());
}

function loadMeta(): Record<string, Track> {
  try { return JSON.parse(localStorage.getItem(META_KEY) || "{}"); } catch { return {}; }
}
function saveMeta(m: Record<string, Track>) {
  try { localStorage.setItem(META_KEY, JSON.stringify(m)); } catch { /* quota: audio is what matters */ }
}

export function rememberOfflineTrack(t: Track) {
  const m = loadMeta();
  m[t.id] = t;
  saveMeta(m);
}

/** Saved tracks, newest first, with their metadata. */
export function offlineTracks(): Track[] {
  const m = loadMeta();
  return listOffline().reverse().map((id) => m[id]).filter(Boolean) as Track[];
}

export function offlineCount(tracks: Track[]): number {
  return tracks.reduce((n, t) => n + (isOffline(t.id) ? 1 : 0), 0);
}

function pump() {
  while (running < CONCURRENCY && queue.length) {
    const { job, track } = queue.shift()!;
    if (cancelled.has(job.key)) continue;
    running++;
    void (async () => {
      let ok = isOffline(track.id);
      if (!ok) ok = await saveTrack(track.id, getAccess()).catch(() => false);
      if (ok) rememberOfflineTrack(track);
      running--;
      if (!cancelled.has(job.key)) {
        if (ok) job.done++; else job.failed++;
        if (job.done + job.failed >= job.ids.length) {
          snap.jobs = snap.jobs.filter((j) => j !== job);
        }
        emit();
      }
      pump();
    })();
  }
}

export function downloadAll(key: string, label: string, tracks: Track[]) {
  if (snap.jobs.some((j) => j.key === key)) return;
  cancelled.delete(key);
  const todo = tracks.filter((t) => !isOffline(t.id));
  tracks.filter((t) => isOffline(t.id)).forEach(rememberOfflineTrack);
  if (!todo.length) { emit(); return; }
  const job: Job = { key, label, ids: todo.map((t) => t.id), done: 0, failed: 0 };
  snap.jobs = [...snap.jobs, job];
  todo.forEach((track) => queue.push({ job, track }));
  emit();
  pump();
}

export function cancelDownload(key: string) {
  cancelled.add(key);
  snap.jobs = snap.jobs.filter((j) => j.key !== key);
  for (let i = queue.length - 1; i >= 0; i--) if (queue[i].job.key === key) queue.splice(i, 1);
  emit();
}

export async function removeAll(tracks: Track[]) {
  const m = loadMeta();
  for (const t of tracks) {
    await removeTrack(t.id);
    delete m[t.id];
  }
  saveMeta(m);
  emit();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function useOfflineJobs(): Snapshot {
  return useSyncExternalStore(subscribe, () => snap, () => snap);
}
